import { computeSupportsReasoning } from "./reasoningModel.js";
import type { AiModelPricing, AiModelRecord } from "./types.js";
import type { OpenRouterModelApi } from "./openrouter.types.js";

function parsePrice(value: string | number | undefined): number {
  if (value === undefined || value === null || value === "") return 0;
  const n = typeof value === "number" ? value : Number.parseFloat(String(value));
  return Number.isFinite(n) ? n : 0;
}

export function extractProviderId(modelId: string): string {
  const slash = modelId.indexOf("/");
  if (slash > 0) return modelId.slice(0, slash);
  return "other";
}

function buildAliases(modelId: string, name?: string): string[] {
  const aliases = new Set<string>();
  if (name) aliases.add(name);
  const slash = modelId.indexOf("/");
  if (slash > 0) {
    const direct = modelId.slice(slash + 1);
    aliases.add(direct);
    const base = direct.replace(/-\d{4}-\d{2}-\d{2}$/, "").replace(/-\d{8}$/, "");
    if (base !== direct) aliases.add(base);
  }
  return [...aliases];
}

function normalizePricing(raw: OpenRouterModelApi["pricing"]): AiModelPricing {
  return {
    promptUsdPerToken: parsePrice(raw.prompt),
    completionUsdPerToken: parsePrice(raw.completion),
    imageUsdPerUnit: parsePrice(raw.image),
    requestUsdPerRequest: parsePrice(raw.request),
    cacheReadUsdPerToken: raw.input_cache_read
      ? parsePrice(raw.input_cache_read)
      : undefined,
    cacheWriteUsdPerToken: raw.input_cache_write
      ? parsePrice(raw.input_cache_write)
      : undefined,
    reasoningUsdPerToken: raw.internal_reasoning
      ? parsePrice(raw.internal_reasoning)
      : undefined,
    webSearchUsdPerRequest: raw.web_search ? parsePrice(raw.web_search) : undefined,
    openRouterMarkupUsdPerInputToken: 0,
    openRouterMarkupUsdPerOutputToken: 0,
    pricedAt: new Date().toISOString(),
    source: "openrouter",
  };
}

function resolveStatus(row: OpenRouterModelApi): AiModelRecord["status"] {
  if (row.expiration_date) {
    const exp = Date.parse(row.expiration_date);
    if (!Number.isNaN(exp) && exp < Date.now()) return "deprecated";
  }
  return "active";
}

export function normalizeOpenRouterModel(row: OpenRouterModelApi, syncedAt: string): AiModelRecord {
  const outputModalities = row.architecture?.output_modalities ?? ["text"];
  const inputModalities = row.architecture?.input_modalities ?? ["text"];
  const supportedParameters = row.supported_parameters ?? [];
  const pricing = normalizePricing(row.pricing);
  const openRouterPricing = row.pricing;
  const supportsReasoning = computeSupportsReasoning({
    supportedParameters,
    pricing,
    openRouterPricing,
  });

  return {
    modelId: row.id,
    name: row.name ?? row.id,
    providerId: extractProviderId(row.id),
    canonicalSlug: row.canonical_slug ?? row.id,
    status: resolveStatus(row),
    description: row.description ?? "",
    created: row.created ?? 0,
    expirationDate: row.expiration_date ?? null,
    contextLength: row.context_length ?? row.top_provider?.context_length ?? 0,
    maxCompletionTokens: row.top_provider?.max_completion_tokens ?? null,
    isModerated: row.top_provider?.is_moderated ?? false,
    modality: row.architecture?.modality ?? "",
    inputModalities,
    outputModalities,
    tokenizer: row.architecture?.tokenizer ?? "",
    instructType: row.architecture?.instruct_type ?? null,
    supportedParameters,
    defaultParameters: row.default_parameters ?? null,
    perRequestLimits: row.per_request_limits ?? null,
    pricing,
    openRouterPricing,
    architecture: row.architecture,
    topProvider: row.top_provider,
    openRouter: row,
    aliases: buildAliases(row.id, row.name),
    availableOnOpenRouter: true,
    supportsStreaming: outputModalities.includes("text"),
    supportsTools: supportedParameters.includes("tools"),
    supportsReasoning,
    primaryOutputModality: outputModalities[0] ?? "text",
    syncedAt,
    syncSource: "openrouter",
  };
}
